import { Avatar, Button, Modal, Spin } from "antd";
import dayjs from "dayjs";
import { last } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiFactory from "../../api";
import deagu from "../../assets/deagu.jpg";
import { conversationTab, conversationType } from "../../config/Constant";
import { useAuthLayoutContext } from "../../context/AuthLayoutContext";
import { useMenuContext } from "../../context/MenuContext";
import { useInfoUser } from "../../store/UserStore";
import { lastTimeOnlineUtils } from "../../utils/formatTime";
import { updateOrg } from "../../utils/OrganizationHelper";
import { getColorFromInitial } from "../../utils/Utils";
import { useImageLoader } from "../customHook/ImageLoader";
import "./UserDetailModal.scss";
import { IoChatbubbleEllipsesOutline } from "react-icons/io5";
import { MdMailOutline } from "react-icons/md";
import { LuPhone, LuVideo } from "react-icons/lu";
import { MemberAvatar } from "./ProfileAvatar";

const UserDetailModal = ({ person, isOpen, handleCancel }) => {
  const navigate = useNavigate();

  /** Context */
  const { setOrgList, openCall } = useAuthLayoutContext();

  const {
    conversationList,
    activeKey,
    handleUpdateConversationList,
    setSelectedConversation,
  } = useMenuContext();
  const { user, languageMap } = useInfoUser();
  const [briefName, setBriefName] = useState();
  const [loadingCreateConversation, setLoadingCreateConversation] =
    useState(false);
  const [partner, setPartner] = useState(person);
  const [countPartnership, setCountPartnership] = useState(0);
  const [errorComponent, setErrorComponent] = useState(<div></div>);
  const {
    imageUrl,
    loading: isImageLoading,
    error: imageError,
  } = useImageLoader(person?.avatar);

  const getPartnerShip = async () => {
    try {
      const rs = await apiFactory.userConversationApi.partnership(
        person?.userId,
      );

      if (!rs || !rs.data || rs.status !== 200) return;

      setCountPartnership(rs.data);
    } catch (error) {}
  };

  const getUserDetailInfo = async () => {
    try {
      if (!person?.userId) return;

      let data = person;

      if (!person?.listOrg?.length) {
        const rs = await apiFactory.userApi.orgUser(person?.userId);

        if (!rs?.data || rs?.status !== 200) return;

        data = rs.data;
      }

      setPartner(data);
      updateOrg(setOrgList, data);
    } catch (error) {
      console.log(error);
    }
  };

  const handleCreateConversation = async () => {
    setLoadingCreateConversation(true);

    const userIds =
      user?.userId === person?.userId
        ? [user?.userId]
        : [user?.userId, person?.userId];

    const type =
      user?.userId === person?.userId
        ? conversationType.SELF
        : conversationType.PERSONAL;

    const result = await apiFactory.conversationApi.findGroup({
      name: "",
      userIds: userIds,
      conversationType: type,
    });

    if (result?.data === null) {
      navigate(`/conversation/virtual/${person?.userId}`);
      setLoadingCreateConversation(false);
      handleCancel();
      return;
    }

    if (result?.data?.type === conversationType.PERSONAL) {
      const isExist = conversationList?.some(
        (item) => item?.conversationId === result?.data?.conversationId,
      );

      if (!isExist && activeKey === conversationTab.ALL) {
        handleUpdateConversationList([result?.data, ...conversationList]);
      } else {
        const updatedList = [
          result?.data,
          ...conversationList.filter(
            (item) => item?.conversationId !== result?.data?.conversationId,
          ),
        ];

        handleUpdateConversationList(updatedList);
      }
    }

    setLoadingCreateConversation(false);
    handleCancel();

    navigate(`/conversation/chat/${result?.data?.conversationId}`);
  };

  const comeToMail = (e) => {
    e.stopPropagation();
    setSelectedConversation({ type: "DRAFT" });
    navigate(`/conversation/draft-mail?recipient=${person?.userId}`);
    handleCancel();
  };

  const getAvatar = useMemo(() => {
    if (isImageLoading) {
      return (
        <Avatar
          className="fb-profile-avatar"
          size={90}
          style={{
            backgroundColor: getColorFromInitial(briefName),
          }}
          icon={<Spin />}
        >
          {briefName}
        </Avatar>
      );
    }

    if (imageError) {
      return (
        <Avatar
          className="fb-profile-avatar"
          size={90}
          style={{
            backgroundColor: getColorFromInitial(briefName),
          }}
        >
          {briefName}
        </Avatar>
      );
    }

    return (
      <Avatar
        className="fb-profile-avatar"
        size={90}
        src={imageUrl}
        style={{
          backgroundColor: getColorFromInitial(briefName),
        }}
      >
        {briefName}
      </Avatar>
    );
  }, [person, imageUrl, isImageLoading, imageError]);

  useEffect(() => {
    if (person) {
      getPartnerShip();
      getUserDetailInfo();

      setBriefName(
        last(person?.name?.trim()?.split(" "))?.[0] ||
          last(person?.username?.trim()?.split(" "))?.[0],
      );
    }
  }, [person?.userId]);

  useEffect(() => {
    setErrorComponent(
      <div
        style={{
          width: 90,
          height: 90,
          borderRadius: "50%",
          backgroundColor: getColorFromInitial(briefName),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 28,
          color: "#fff",
          fontWeight: 500,
          userSelect: "none",
          border: "3px solid white",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        }}
      >
        {briefName}
      </div>,
    );
  }, [briefName]);

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      width={450}
      className="no-padding-modal"
      styles={{
        body: {
          padding: 0,
          backgroundColor: "#f0f2f5",
        },
      }}
    >
      <div className="relative">
        <div className="h-[200px]">
          <img src={deagu} alt="Cover" className="w-full h-full object-cover" />
        </div>

        <div className="relative px-6 pb-[10px] pt-0">
          <div className="flex items-center gap-[16px] mt-[-30px]">
            <MemberAvatar
              person={person}
              size={90}
              onlineSize="big"
              isAction={null}
            />
            <div className="flex flex-col mt-[30px]">
              <b className="text-[17px]">{person?.name || "UNKNOWN"}</b>
              <div className="text-[15px] text-[#333d4b]">
                {!partner?.online &&
                  lastTimeOnlineUtils(partner, languageMap, user?.language)}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-[15px] py-[20px]">
            <Button
              className="bg-[#2a56b9] text-[white]"
              loading={loadingCreateConversation}
              onClick={handleCreateConversation}
            >
              <IoChatbubbleEllipsesOutline className="text-[18px]" />
            </Button>
            <Button
              className="bg-[#2a56b9] text-[white]"
              disabled={loadingCreateConversation}
              onClick={comeToMail}
            >
              <MdMailOutline className="text-[18px]" />
            </Button>

            {user?.userId !== person?.userId && (
              <>
                <Button
                  className="bg-[#2a56b9] text-[white]"
                  onClick={() => openCall([person?.userId], "audio")}
                >
                  <LuPhone className="text-[18px]" />
                </Button>
                <Button
                  className="bg-[#2a56b9] text-[white]"
                  onClick={() => openCall([person?.userId], "video")}
                >
                  <LuVideo className="text-[18px]" />
                </Button>
              </>
            )}
          </div>
          <div className="info-card">
            <div className="text-[18px] font-semibold">
              {languageMap?.["modal.detail.label.userInfo"] ??
                "User information"}
            </div>
            <div className="flex flex-col gap-[10px]">
              <div className="flex gap-[10px]">
                <div className="text-[15px] text-[#333d4b] w-[90px]">
                  {languageMap?.["modal.detail.label.userCode"] ?? "User id"}
                </div>
                <div className="text-[15px] font-semibold">
                  {partner?.userId || "..."}
                </div>
              </div>
              <div className="flex gap-[10px]">
                <div className="text-[15px] text-[#333d4b] w-[90px]">
                  {languageMap?.["menu.profile.position"] ?? "Position"}
                </div>
                <div className="text-[15px] font-semibold">
                  {partner?.position || "..."}
                </div>
              </div>
              <div className="flex gap-[10px]">
                <div className="text-[15px] text-[#333d4b] w-[90px]">
                  {languageMap?.["modal.detail.label.phone"] ?? "Phone"}
                </div>
                <div className="text-[15px] font-semibold">
                  {partner?.phone || "..."}
                </div>
              </div>
              <div className="flex gap-[10px]">
                <div className="text-[15px] text-[#333d4b] w-[90px]">
                  {languageMap?.["modal.detail.label.mood"] ?? "Mood"}
                </div>
                <div className="text-[15px] font-semibold">
                  {partner?.mood || "..."}
                </div>
              </div>
              <div className="flex gap-[10px]">
                <div className="text-[15px] text-[#333d4b] w-[90px]">
                  {languageMap?.["modal.detail.label.birthday"] ?? "Birthday"}
                </div>
                <div className="text-[15px] font-semibold">
                  {partner?.birthday
                    ? dayjs(partner?.birthday).format("YYYY-MM-DD")
                    : "..."}
                </div>
              </div>
              <div className="flex gap-[10px]">
                <div className="text-[15px] text-[#333d4b] w-[90px]">
                  {languageMap?.["modal.detail.label.email"] ?? "Email"}
                </div>
                <div className="text-[15px] font-semibold">
                  {partner?.email || "..."}
                </div>
              </div>
            </div>
          </div>
          <div className="info-card">
            <div className="text-[18px] font-semibold">
              {languageMap?.["modal.detail.label.org"] ?? "Organization"}
            </div>

            {person?.paths?.length ? (
              <b className="text-[#2a56b9]">
                {person?.paths?.map((path) => path?.name)?.join(" > ")}
              </b>
            ) : (
              <div className="text-[15px] text-[#333d4b]">
                No in any departments
              </div>
            )}
          </div>
          {/*<div className="info-card">*/}
          {/*  <button*/}
          {/*    className="fb-menu-option"*/}
          {/*    onClick={() => {*/}
          {/*    }}*/}
          {/*  >*/}
          {/*    <WechatOutlined className="fb-menu-icon" />*/}
          {/*    <span className="fb-menu-text">*/}
          {/*      {languageMap?.["modal.detail.label.generalChatGr"] ??*/}
          {/*        "General chat group"}{" "}*/}
          {/*      ({countPartnership?.conversationCount || 0})*/}
          {/*    </span>*/}
          {/*  </button>*/}

          {/*  <button*/}
          {/*    className="fb-menu-option"*/}
          {/*    onClick={() => {*/}
          {/*    }}*/}
          {/*  >*/}
          {/*    <MailOutlined className="fb-menu-icon" />*/}
          {/*    <span className="fb-menu-text">*/}
          {/*      {languageMap?.["modal.detail.label.generalMessageGr"] ??*/}
          {/*        "General message group"}{" "}*/}
          {/*      ({countPartnership?.messageCount || 0})*/}
          {/*    </span>*/}
          {/*  </button>*/}
          {/*</div>*/}
        </div>
      </div>
    </Modal>
  );
};

export { UserDetailModal };
